import { React, useMemo, useState, useEffect } from "react";
import { useAsyncDebounce } from "react-table";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendarAlt } from "react-icons/fa";
import { forwardRef } from "react";

// Helper
const customSelectStyles = {
  control: (provided) => ({ ...provided, minHeight: "0" }),
  indicatorsContainer: (provided) => ({ ...provided, padding: "0" }),
};

const multiStatusSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "38px",
    minWidth: "220px",
    maxWidth: "400px",
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: "#e8f0fe",
    borderRadius: "4px",
    fontSize: "0.85rem",
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: "#1a73e8",
    fontWeight: 500,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: "#1a73e8",
    ":hover": { backgroundColor: "#d2e3fc", color: "#174ea6" },
  }),
  valueContainer: (provided) => ({
    ...provided,
    maxHeight: "80px",
    overflowY: "auto",
    flexWrap: "wrap",
  }),
};

const multiAccountSelectStyles = {
  ...multiStatusSelectStyles,
  control: (provided) => ({
    ...provided,
    minHeight: "38px",
    minWidth: "180px",
    maxWidth: "350px",
  }),
};

const dateOptions = [
  { label: "All", value: "" },
  { label: "This Week", value: "1" },
  { label: "This Month", value: "2" },
  { label: "Last 30 Days", value: "3" },
  { label: "Last 3 Months", value: "4" },
  { label: "Year to Date", value: "5" },
];

export const formatDate = (d) => {
  return (
    [d.getMonth() + 1, d.getDate(), d.getFullYear()].join("/") +
    " " +
    [d.getHours(), d.getMinutes(), d.getSeconds()].join(":")
  );
};

export function GlobalFilter({ value, onChange }) {
  const [input, setInput] = useState(value || "");

  const debouncedChange = useAsyncDebounce((inputValue) => {
    sessionStorage.setItem("globalFilter", inputValue || "");
    onChange(inputValue || "");
  }, 200);

  useEffect(() => {
    onChange(input); // Trigger initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="searchbar">
      <input
        value={input || ""}
        onChange={(e) => {
          setInput(e.target.value);
          debouncedChange(e.target.value);
        }}
        placeholder="Search"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Status options (Shipments vs Home)
// ─────────────────────────────────────────────────────────────

export const shipmentStatusOptions = [
  { value: "submitted", label: "Awaiting Arrival" },
  { value: "bill on file", label: "Bill On File" },
  { value: "CBP DETAINED", label: "CBP Detained" },
  { value: "CBP DOCS REVIEW HOLD", label: "CBP Docs Review Hold" },
  { value: "CBP INTENSIVE EXAM", label: "CBP Intensive Exam" },
  { value: "CBP VACIS EXAM", label: "CBP Vacis Exam" },
  { value: "pending documents", label: "Documents Required" },
  { value: "ENTRY CANCELED", label: "Entry Canceled" },
  { value: "ISF HOLD", label: "ISF Hold" },
  { value: "IT SHIPMENT", label: "IT Shipment" },
  { value: "no bill match", label: "No Bill Match" },
  { value: "NOT CLEARED BY AFB", label: "Not Cleared By AFB" },
  { value: "ASSEMBLY", label: "Pending Release" },
  { value: "PENDING REVIEW", label: "Pending Review" },
  { value: "released", label: "Released" },
];

export const homeShipmentStatusOptions = [
  { value: "bill on file", label: "Bill On File" },
  { value: "CBP DETAINED", label: "CBP Detained" },
  { value: "CBP DOCS REVIEW HOLD", label: "CBP Docs Review Hold" },
  { value: "CBP INTENSIVE EXAM", label: "CBP Intensive Exam" },
  { value: "CBP VACIS EXAM", label: "CBP Vacis Exam" },
  { value: "pending documents", label: "Documents Required" },
  { value: "ISF HOLD", label: "ISF Hold" },
  { value: "IT SHIPMENT", label: "IT Shipment" },
  { value: "no bill match", label: "No Bill Match" },
  { value: "PENDING RELEASE", label: "Pending Release" },
  { value: "RELEASED", label: "Released" },
];

// ─────────────────────────────────────────────────────────────
// Mode filter options (All vs Air Shipments)
// ─────────────────────────────────────────────────────────────

export const modeOptions = [
  { value: "air", label: "Air" },
  { value: "ocean", label: "Ocean" },
  { value: "rail", label: "Rail" },
  { value: "trucker", label: "Trucker" },
];

// ─────────────────────────────────────────────────────────────
// Status + Account filter
// ─────────────────────────────────────────────────────────────

export function StatusFilter({
  statusValue,
  accountValue,
  onStatusChange,
  onAccountChange,
  accounts,
  statusOptions,
}) {
  const selectOptions = statusOptions || shipmentStatusOptions;

  const accountList = Array.isArray(accounts)
    ? accounts
    : (accounts || "").split(",");

  const accountOptions = accountList
    .map((account) => String(account).trim())
    .filter(Boolean)
    .map((account) => ({
      label: account,
      value: account,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // statusValue and accountValue are now arrays
  const selectedStatuses = Array.isArray(statusValue) ? statusValue : [];
  const selectedAccounts = Array.isArray(accountValue) ? accountValue : [];

  return (
    <div className="status-filter" style={{ display: "flex", gap: "10px" }}>
      {/* Shipment Status — multi-select */}
      <Select
        styles={multiStatusSelectStyles}
        isMulti
        value={selectOptions.filter((opt) => selectedStatuses.includes(opt.value))}
        onChange={(opts) => {
          const values = (opts || []).map((o) => o.value);
          sessionStorage.setItem("statusFilter", JSON.stringify(values));
          onStatusChange(values);
        }}
        options={selectOptions}
        className="custom-select"
        placeholder="Shipment Status"
        isClearable
      />

      <Select
        styles={multiAccountSelectStyles}
        isMulti
        value={accountOptions.filter((opt) => selectedAccounts.includes(opt.value))}
        onChange={(opts) => {
          const values = (opts || []).map((o) => o.value);
          sessionStorage.setItem("accountFilter", JSON.stringify(values));
          onAccountChange(values);
        }}
        options={accountOptions}
        className="custom-select"
        placeholder="Account"
        isClearable
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Mode filter component
// ─────────────────────────────────────────────────────────────

export function ModeFilter({ value, onChange }) {
  // value from parent is an array of selected mode strings, or "" / null for all
  const selectedValues = Array.isArray(value)
    ? value
    : value
    ? [value]
    : [];

  const selected = modeOptions.filter((o) => selectedValues.includes(o.value));

  const handleChange = (opts) => {
    const values = (opts || []).map((o) => o.value);
    onChange(values); // pass array back to parent
  };

  return (
    <Select
      styles={multiStatusSelectStyles}
      isMulti
      value={selected}
      onChange={handleChange}
      options={modeOptions}
      className="custom-select"
      placeholder="Shipment type"
      isClearable
    />
  );
}

// ─────────────────────────────────────────────────────────────
// Date range filter
// ─────────────────────────────────────────────────────────────

const toStartOfDayISO = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
};

const toEndOfDayISO = (d) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
};

const getDateRangeLabel = (start, end) => {
  if (!start || !end) return "";
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  const s = `${monthNames[start.getMonth()]} ${start.getDate()}`;
  const e = `${monthNames[end.getMonth()]} ${end.getDate()}`;
  return `${s} - ${e}`;
};

const CalendarIconButton = forwardRef(({ onClick, title }, ref) => {
  return (
    <button
      type="button"
      className="date-filter-icon-btn"
      onClick={onClick}
      ref={ref}
      title={title || "Date Filter"}
      aria-label={title || "Date Filter"}
    >
      <FaRegCalendarAlt />
    </button>
  );
});

const DateFilterCalendarContainer = ({ className, children, clear, canClear }) => {
  return (
    <div className={className}>
      <div className="date-filter-popover-header">
        {canClear ? (
          <button
            type="button"
            className="date-filter-popover-clear-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clear();
            }}
            aria-label="Clear date filter"
            title="Clear"
          >
            ×
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
};


export function DateRangeFilter({ value, onChange }) {
  // value is expected to be [startISO, endISO] or []
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // view: 'day' | 'month' | 'year'
  const [calendarView, setCalendarView] = useState('day');
  const [open, setOpen] = useState(false);

  // Restore from sessionStorage once (like your previous select behavior)
  useEffect(() => {
    const saved = sessionStorage.getItem("dateFilterRange");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const s = parsed?.start ? new Date(parsed.start) : null;
        const e = parsed?.end ? new Date(parsed.end) : null;
        setStartDate(s);
        setEndDate(e);

        // trigger initial load
        if (s && e) onChange([toStartOfDayISO(s), toEndOfDayISO(e)]);
        else onChange([]);
        return;
      } catch {
        // ignore
      }
    }

    // fallback: hydrate from parent
    if (value?.length === 2) {
      const s = value[0] ? new Date(value[0]) : null;
      const e = value[1] ? new Date(value[1]) : null;
      setStartDate(s);
      setEndDate(e);
      if (s && e) onChange([toStartOfDayISO(s), toEndOfDayISO(e)]);
      else onChange([]);
    } else {
      onChange([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep local UI in sync if parent changes later (URL/session hydration, etc.)
  useEffect(() => {
    if (value?.length === 2) {
      const s = value[0] ? new Date(value[0]) : null;
      const e = value[1] ? new Date(value[1]) : null;
      setStartDate(s);
      setEndDate(e);
    } else if (!value || value.length === 0) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [value?.[0], value?.[1]]);

  const persist = (s, e) => {
    sessionStorage.setItem(
      "dateFilterRange",
      JSON.stringify({
        start: s ? s.toISOString() : null,
        end: e ? e.toISOString() : null,
      })
    );
  };

  const clear = () => {
    setStartDate(null);
    setEndDate(null);
    sessionStorage.removeItem("dateFilterRange");
    onChange([]);
  };

  const label = getDateRangeLabel(startDate, endDate);

  // Custom header for calendar view switching
  const renderCustomHeader = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    if (calendarView === 'day') {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px' }}>
          <button type="button" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>{'<'}</button>
          <span style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => setCalendarView('month')}>
            {monthNames[date.getMonth()]} {date.getFullYear()}
          </span>
          <button type="button" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>{'>'}</button>
        </div>
      );
    }
    if (calendarView === 'month') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0 8px' }}>
          <span style={{ cursor: 'pointer', fontWeight: 'bold' }} onClick={() => setCalendarView('year')}>
            {date.getFullYear()}
          </span>
        </div>
      );
    }
    if (calendarView === 'year') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0 8px' }}>
          <span style={{ fontWeight: 'bold' }}>Select Year</span>
        </div>
      );
    }
    return null;
  };

  // Props for DatePicker based on view
  let pickerProps = {
    selectsRange: true,
    startDate,
    endDate,
    onChange: (dates) => {
      const [s, e] = dates;
      setStartDate(s);
      setEndDate(e);
      persist(s, e);
      if (s && e) {
        onChange([toStartOfDayISO(s), toEndOfDayISO(e)]);
        setOpen(false); // Only close after both dates selected in day view
      } else {
        onChange([]);
      }
    },
    isClearable: false,
    dateFormat: "MMMM dd",
    placeholderText: label,
    customInput: <CalendarIconButton title={label || "Date Filter"} />,
    popperPlacement: "bottom-start",
    calendarContainer: (props) => (
      <DateFilterCalendarContainer
        {...props}
        clear={clear}
        canClear={!!startDate || !!endDate}
      />
    ),
    renderCustomHeader,
    open: open,
    onClickOutside: () => setOpen(false),
    onInputClick: () => setOpen(true),
    onSelect: undefined, // We'll set this below for month/year
  };

  if (calendarView === 'month') {
    pickerProps.showMonthYearPicker = true;
    pickerProps.dateFormat = "MMMM yyyy";
    pickerProps.onChange = (date) => {
      // When a month is picked, go to day view for that month
      // Do NOT set startDate here, just switch view
      setCalendarView('day');
    };
    pickerProps.selectsRange = false;
    pickerProps.selected = startDate || new Date(); // fallback for calendar display
    pickerProps.onSelect = undefined;
  } else if (calendarView === 'year') {
    pickerProps.showYearPicker = true;
    pickerProps.dateFormat = "yyyy";
    pickerProps.onChange = (date) => {
      // When a year is picked, go to month view for that year
      // Do NOT set startDate here, just switch view
      setCalendarView('month');
    };
    pickerProps.selectsRange = false;
    pickerProps.selected = startDate || new Date();
    pickerProps.onSelect = undefined;
  }

  return (
    <div className="date-filter" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <DatePicker {...pickerProps} />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Date filter function for react-table
// ─────────────────────────────────────────────────────────────

const dateKeyFromDb = (raw) => {
  if (!raw) return null;

  const str = String(raw).trim();
  if (!str || str.startsWith("0000-00-00")) return null;

  // DB format: "YYYY-MM-DD HH:mm:ss"
  const datePart = str.slice(0, 10); // "YYYY-MM-DD"
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;

  const [y, m, d] = datePart.split("-").map(Number);
  return y * 10000 + m * 100 + d; // YYYYMMDD
};

const dateKeyFromValue = (v) => {
  if (!v) return null;

  // If DatePicker ever passes Date objects
  if (v instanceof Date) {
    return v.getFullYear() * 10000 + (v.getMonth() + 1) * 100 + v.getDate();
  }

  const s = String(v);

  // If it's ISO (what you're currently storing)
  if (s.includes("T")) {
    const d = new Date(s);
    if (isNaN(d)) return null;
    return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  }

  // If it's DB-like
  return dateKeyFromDb(s);
};

export function dateBetweenFilterFn(rows, id, filterValues) {
  const sdKey = dateKeyFromValue(filterValues?.[0]);
  const edKey = dateKeyFromValue(filterValues?.[1]);

  if (!sdKey && !edKey) return rows;

  return rows.filter((r) => {
    // prefer raw payload to avoid react-table value transformations
    const raw = r?.original?.est_time_arrive ?? r?.values?.[id];
    const cellKey = dateKeyFromDb(raw);
    if (!cellKey) return false;

    if (sdKey && edKey) return cellKey >= sdKey && cellKey <= edKey;
    if (sdKey) return cellKey >= sdKey;
    if (edKey) return cellKey <= edKey;
    return true;
  });
}


// ─────────────────────────────────────────────────────────────
// Default column filter + SelectColumnFilter
// ─────────────────────────────────────────────────────────────

export function DefaultFilterForColumn({
  column: {
    filterValue,
    preFilteredRows: { length },
    setFilter,
  },
}) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => {
        // Set undefined to remove the filter entirely
        setFilter(e.target.value || undefined);
      }}
      placeholder={`Search ${length} records..`}
      style={{ marginTop: "10px" }}
    />
  );
}

// Component for Custom Select Filter
export function SelectColumnFilter({
  column: { filterValue, setFilter, preFilteredRows, id },
}) {
  // Use preFilteredRows to calculate the options
  const options = useMemo(() => {
    const opts = new Set();
    preFilteredRows.forEach((row) => {
      opts.add(row?.values[id]);
    });
    return [...opts.values()].sort((a, b) => String(a || "").localeCompare(String(b || "")));
  }, [id, preFilteredRows]);

  // UI for Multi-Select box
  return (
    <select
      value={filterValue || ""}
      onChange={(e) => {
        setFilter(e?.target?.value || undefined);
      }}
    >
      <option value="">All</option>
      {options.map((option, i) => (
        <option key={i} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}
